/**
 * Lightweight markdown renderer — no external dependencies.
 * Supports: # / ## / ### / #### headings, **bold**, *italic*, _italic_,
 * `inline code`, - bullet lists, numbered lists, blank-line paragraphs,
 * links [t](u), pipe-tables, and common HTML entities (&ldquo; &rdquo;
 * &lsquo; &rsquo; &mdash; &ndash; &hellip; &amp; &nbsp; &copy; &reg;).
 */
import { Fragment } from "react";

const ENTITY_MAP = {
  "&ldquo;": "\u201C", "&rdquo;": "\u201D",
  "&lsquo;": "\u2018", "&rsquo;": "\u2019",
  "&mdash;": "\u2014", "&ndash;": "\u2013",
  "&hellip;": "\u2026", "&nbsp;": "\u00A0",
  "&copy;": "\u00A9", "&reg;": "\u00AE",
  "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": "\"",
};
function decodeEntities(s) {
  return (s || "").replace(/&(?:ldquo|rdquo|lsquo|rsquo|mdash|ndash|hellip|nbsp|copy|reg|amp|lt|gt|quot);/g, (m) => ENTITY_MAP[m] || m);
}

function renderInline(text) {
  const decoded = decodeEntities(text);
  const parts = [];
  let i = 0;
  const push = (node) => parts.push(node);
  // **bold** | *italic* | _italic_ | `code` | [text](url)
  const re = /(\*\*[^*]+\*\*|\*[^*\n]+\*|_[^_\n]+_|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let m;
  let last = 0;
  while ((m = re.exec(decoded))) {
    if (m.index > last) push(decoded.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) push(<strong key={i++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`")) push(<code key={i++} className="px-1 py-0.5 bg-muted font-mono text-xs">{tok.slice(1, -1)}</code>);
    else if (tok.startsWith("[")) {
      const inner = /\[([^\]]+)\]\(([^)]+)\)/.exec(tok);
      if (inner) push(<a key={i++} href={inner[2]} className="underline text-primary">{inner[1]}</a>);
    } else if (tok.startsWith("*")) push(<em key={i++}>{tok.slice(1, -1)}</em>);
    else if (tok.startsWith("_")) push(<em key={i++} className="text-muted-foreground">{tok.slice(1, -1)}</em>);
    last = m.index + tok.length;
  }
  if (last < decoded.length) push(decoded.slice(last));
  return parts;
}

// Detects a pipe-table starting at `lines[i]`. Returns [tableNode, nextIndex]
// or [null, i] if not a table.
function tryParseTable(lines, i, keyStart) {
  const header = lines[i];
  const sep = lines[i + 1];
  if (!/^\s*\|.+\|\s*$/.test(header || "")) return [null, i];
  if (!/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(sep || "")) return [null, i];
  const splitRow = (row) =>
    row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
  const heads = splitRow(header);
  const rows = [];
  let j = i + 2;
  while (j < lines.length && /^\s*\|.+\|\s*$/.test(lines[j])) {
    rows.push(splitRow(lines[j]));
    j++;
  }
  let key = keyStart;
  const node = (
    <div key={key++} className="my-4 overflow-x-auto border border-border">
      <table className="w-full text-sm">
        <thead className="bg-muted/60">
          <tr>
            {heads.map((h, hi) => (
              <th key={hi} className="text-left font-semibold px-3 py-2 border-b border-border">{renderInline(h)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri} className="odd:bg-background even:bg-muted/20">
              {r.map((c, ci) => (
                <td key={ci} className="px-3 py-2 border-b border-border/50 align-top">{renderInline(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return [node, j];
}

export default function Markdown({ children, className = "" }) {
  const lines = (children || "").split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (/^####\s/.test(line)) {
      out.push(<h4 key={key++} className="text-base font-bold mt-6 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{renderInline(line.replace(/^####\s/, ""))}</h4>);
      i++; continue;
    }
    if (/^###\s/.test(line)) {
      out.push(<h3 key={key++} className="text-lg font-bold mt-8 mb-2" style={{ fontFamily: "var(--font-heading)" }}>{renderInline(line.replace(/^###\s/, ""))}</h3>);
      i++; continue;
    }
    if (/^##\s/.test(line)) {
      out.push(<h2 key={key++} className="text-2xl font-bold mt-10 mb-3" style={{ fontFamily: "var(--font-heading)" }}>{renderInline(line.replace(/^##\s/, ""))}</h2>);
      i++; continue;
    }
    if (/^#\s/.test(line)) {
      out.push(<h1 key={key++} className="text-3xl font-bold mt-12 mb-4" style={{ fontFamily: "var(--font-heading)" }}>{renderInline(line.replace(/^#\s/, ""))}</h1>);
      i++; continue;
    }

    // list block
    if (/^[-*]\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s/.test(lines[i])) {
        items.push(<li key={key++} className="ml-5 list-disc">{renderInline(lines[i].replace(/^[-*]\s/, ""))}</li>);
        i++;
      }
      out.push(<ul key={key++} className="my-3 space-y-1">{items}</ul>);
      continue;
    }
    if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={key++} className="ml-5 list-decimal">{renderInline(lines[i].replace(/^\d+\.\s/, ""))}</li>);
        i++;
      }
      out.push(<ol key={key++} className="my-3 space-y-1">{items}</ol>);
      continue;
    }

    // pipe table
    if (/^\s*\|.+\|\s*$/.test(line)) {
      const [tableNode, next] = tryParseTable(lines, i, key);
      if (tableNode) {
        out.push(tableNode);
        key += 1;
        i = next;
        continue;
      }
    }

    // blank line
    if (line.trim() === "") {
      i++; continue;
    }

    // paragraph (collect consecutive non-blank, non-special lines)
    const para = [];
    while (i < lines.length && lines[i].trim() !== "" && !/^(#{1,4}\s|[-*]\s|\d+\.\s)/.test(lines[i])) {
      para.push(lines[i]);
      i++;
    }
    out.push(
      <p key={key++} className="my-3 leading-relaxed text-foreground/90">
        {para.map((p, idx) => (
          <Fragment key={idx}>
            {renderInline(p)}
            {idx < para.length - 1 && <br />}
          </Fragment>
        ))}
      </p>
    );
  }

  return <div className={className}>{out}</div>;
}
